import React, { useEffect, useState } from "react";
import {
	IconButton,
	Tooltip,
	Typography,
	Link,
	Button,
	Grid,
	LinearProgress,
	Box,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { CommentIcon } from "utility/icons";
import "./WorkFlowApprovalStepsHistory.css";
import { RequisitionService } from "services/requisition.service";
import { Shared } from "utility/shared";
import { IRequisitionStepHistory } from "models/Requisition/RequisitionStepHistory";
import Emitter from "utility/emitter";
import { RequisitionStepHistoryStatus } from "utility/enums/requisition-entry";
import ResponsiveDialog from "components/ResponsiveDialog";
import { RequisitionStepHistoryEmitter } from "models/Requisition/RequisitionStepHistoryEmitter";
import { RootState } from "redux/store";
import { IStepHistoryComment } from "models/StepHistoryDetail";
import { CustomTooltip } from "components/CustomTooltip";
import {
	IS_REQUISITION_ASSIGNED_TO_USER,
	OPEN_REQUISITION_STEP_HISTORY_DETAIL,
} from "utility/emitterEvents";
import { AccessRightName, MenuName } from "utility/enums/user-access-right";
import { ActionType } from "utility/enums/action-type";
import { AppRoutings } from "utility/enums/app-routings";
import useAccessRight from "hooks/useAccessRight";
import { Loading } from "components/Loading";

type Props = {
	requisitionDetail: RequisitionStepHistoryEmitter;
	openProcurementMethodSelectionWindow: (
		showProcurementMethodSelection: boolean, // if true, show procurement method selection window
		isEditMode: boolean, // if true, display procurement method selection window in edit mode
		requisitionStepHistoryId: number
	) => void; // required to manage procurement method selection window
};

const WorkFlowApprovalStepsHistory: React.FC<Props> = ({
	requisitionDetail,
	openProcurementMethodSelectionWindow,
}) => {
	const navigate = useNavigate();
	const userAccessRight = useAccessRight();
	const userDetails = useSelector(
		(state: RootState) => state.authState.userDetails
	);
	const [requisitionStepHistoryList, setRequisitionStepHistoryList] = useState<
		IRequisitionStepHistory[]
	>([]);
	const [openInfoDialog, setOpenInfoDialog] = useState<{
		message: string;
		open: boolean;
	}>({
		message: "",
		open: false,
	});
	const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const isRequisitionStepAssignedToUser = (step: IRequisitionStepHistory) => {
		return step.assignToUsers && step.assignToUsers.length > 0
			? step.assignToUsers.findIndex((r) => r.userId === userDetails.userId) >
					-1
			: false;
	};

	const openRequisitionStepHistoryDetail = (item: IRequisitionStepHistory) => {
		showHideProcurementMethodSelectionWindow(
			item.isShowProcurementMethodSelection,
			item.isEditableProcurementMethodSelection,
			item.requisitionStepHistoryId
		);
		Emitter.off(OPEN_REQUISITION_STEP_HISTORY_DETAIL, {} as any);
		Emitter.emit(OPEN_REQUISITION_STEP_HISTORY_DETAIL, item);
	};

	const showHideProcurementMethodSelectionWindow = (
		isViewMode: boolean,
		isEditMode: boolean,
		requisitionStepHistoryId: number
	) => {
		openProcurementMethodSelectionWindow(
			isViewMode,
			isEditMode,
			requisitionStepHistoryId
		);
	};

	const setPrevStepAsSelected = (
		stepHistoryList: IRequisitionStepHistory[],
		filteredList: IRequisitionStepHistory[]
	) => {
		for (let i = 0; i < filteredList.length; i++) {
			const element = filteredList[i];
			if (
				!element.budgetChangeOrderNumber &&
				isRequisitionStepAssignedToUser(element)
			) {
				const index = stepHistoryList.findIndex(
					(r) => r.requisitionStepHistoryId === element.requisitionStepHistoryId
				);
				if (index > -1) {
					setActiveStepIndex(index);
					openRequisitionStepHistoryDetail(stepHistoryList[index]);
					Emitter.emit(
						IS_REQUISITION_ASSIGNED_TO_USER,
						isRequisitionStepAssignedToUser(stepHistoryList[index])
					);
					break;
				}
			}
			// if no prev step is assigned to user, set first step as selected
			if (filteredList.length - 1 === i) {
				setActiveStepIndex(0);
				openRequisitionStepHistoryDetail(stepHistoryList[0]);
			}
		}
	};

	useEffect(() => {
		// get RequisitionStepHistoryList
		if (requisitionDetail.requisitionId > -1) {
			setIsLoading(true);
			setActiveStepIndex(-1);
			showHideProcurementMethodSelectionWindow(false, false, -1);
			RequisitionService.getRequisitionStepHistoryList(
				requisitionDetail.requisitionId
			)
				.then((result) => {
					setRequisitionStepHistoryList(result);
					const index = result.findIndex(
						(r) =>
							r.requisitionStepHistoryId ===
							requisitionDetail.requisitionStepHistoryId
					);
					if (index > -1) {
						if (
							isRequisitionStepAssignedToUser(result[index]) &&
							!result[index].budgetChangeOrderNumber
						) {
							setActiveStepIndex(index);
							showHideProcurementMethodSelectionWindow(
								result[index].isShowProcurementMethodSelection,
								result[index].isEditableProcurementMethodSelection,
								result[index].requisitionStepHistoryId
							);
							Emitter.emit(
								IS_REQUISITION_ASSIGNED_TO_USER,
								isRequisitionStepAssignedToUser(result[index])
							);
						} else {
							setPrevStepAsSelected(
								result,
								JSON.parse(JSON.stringify(result)).slice(0, index).reverse()
							);
						}
					} else {
						setPrevStepAsSelected(
							result,
							JSON.parse(JSON.stringify(result)).reverse()
						);
					}
					setIsLoading(false);
				})
				.catch((err) => {
					Shared.showSnackbarMessage(
						"Error loading requisition step history list, please try again"
					);
					setIsLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		requisitionDetail.requisitionId,
		requisitionDetail.requisitionStepHistoryId,
	]);

	const getClassName = (status: string) => {
		let classes: string = "steps-root ";
		if (status === RequisitionStepHistoryStatus.Ready) {
			classes += "in-progress";
		}
		if (status === RequisitionStepHistoryStatus.Completed) {
			classes += "complete";
		}
		if (status === RequisitionStepHistoryStatus.Recalled) {
			classes += "recall";
		}
		if (status === RequisitionStepHistoryStatus.ChangeRequested) {
			classes += "change-request";
		}
		if (status === RequisitionStepHistoryStatus.Rejected) {
			classes += "reject";
		}
		if (status === RequisitionStepHistoryStatus.Deleted) {
			classes += "delete";
		}
		if (status === RequisitionStepHistoryStatus.Delegated) {
			classes += "delegate";
		}
		if (status === RequisitionStepHistoryStatus.Escalated) {
			classes += "escalate";
		}
		if (status === RequisitionStepHistoryStatus.Cancelled) {
			classes += "cancel";
		}
		return classes;
	};

	const handleStepDetailClick = (
		item: IRequisitionStepHistory,
		index: number
	) => {
		if (activeStepIndex === index) {
			return;
		}
		if (item.budgetChangeOrderNumber) {
			if (
				item.requisitionStepHistoryStatusName ===
				RequisitionStepHistoryStatus.Pending
			)
				setOpenInfoDialog({
					open: true,
					message: `The step selected ${item.stepNumber}, has not been completed and there is no information to display for the step.`,
				});
			else if (!isRequisitionStepAssignedToUser(item))
				setOpenInfoDialog({
					open: true,
					message: `You do not have permission to access the step ${item.stepNumber}.`,
				});
			else openChangeOrderDetail(item);
			return;
		}
		if (
			item.requisitionStepHistoryStatusName ===
				RequisitionStepHistoryStatus.Pending ||
			(item.requisitionStepHistoryStatusName ===
				RequisitionStepHistoryStatus.Ready &&
				!isRequisitionStepAssignedToUser(item))
		) {
			setOpenInfoDialog({
				open: true,
				message: `The step selected ${item.stepNumber}, has not been completed and there is no information to display for the step.`,
			});
		} else {
			setActiveStepIndex(index);
			openRequisitionStepHistoryDetail(item);
			Emitter.emit(
				IS_REQUISITION_ASSIGNED_TO_USER,
				isRequisitionStepAssignedToUser(item)
			);
		}
	};

	// To open change order detail
	const openChangeOrderDetail = (stepDetail: IRequisitionStepHistory) => {
		if (
			userAccessRight.hasAccessRight(
				MenuName.BudgetChangeOrders,
				AccessRightName.View
			)
		) {
			navigate(AppRoutings.BudgetChangeOrderDetail, {
				state: {
					requisitionId: stepDetail.requisitionId,
					budgetChangeOrderNumber: stepDetail.budgetChangeOrderNumber,
					actionType: userAccessRight.hasAccessRight(
						MenuName.BudgetChangeOrders,
						AccessRightName.Update
					)
						? ActionType.Edit
						: ActionType.View,
				},
			});
		} else {
			setOpenInfoDialog({
				open: true,
				message:
					"You do not have permission to view budget change order detail.",
			});
		}
	};

	return (
		<div className="history-panel">
			<div className="heading-part">
				<Typography variant="h4">Workflow approval Steps - History</Typography>
				{requisitionStepHistoryList.length > 0 && (
					<div className="stepper-wrapper">
						<Typography variant="caption">
							{`${requisitionStepHistoryList[0].stepCompletedInPercentage}% Completed`}
						</Typography>
						<Box sx={{ ml: 1, maxWidth: 400, flexGrow: 1 }}>
							<LinearProgress
								variant="determinate"
								value={requisitionStepHistoryList[0].stepCompletedInPercentage}
							/>
						</Box>
					</div>
				)}
			</div>
			<div className="steps-wrapper">
				{!requisitionStepHistoryList.length &&
					(isLoading ? (
						<Loading isWorkflowStepLoader={true} />
					) : (
						<Typography
							className="steps-root"
							variant="body1"
							justifyContent="center"
						>
							No Data Available
						</Typography>
					))}
				{requisitionStepHistoryList.length > 0 &&
					requisitionStepHistoryList.map(
						(item: IRequisitionStepHistory, index: number) => (
							<Link
								key={index}
								onClick={(e) => {
									e.preventDefault();
									handleStepDetailClick(item, index);
								}}
								underline="none"
								className={`${getClassName(
									item.requisitionStepHistoryStatusName
								)} ${index === activeStepIndex && "active"}`}
								style={{
									cursor: "pointer",
								}}
							>
								<Tooltip
									title={item.requisitionStepHistoryStatusName}
									placement="bottom"
								>
									<div
										className="requisition-steps-icon"
										style={
											item.stepNumber.length > 3 ? { fontSize: "12px" } : {}
										}
									>
										{item.stepNumber.includes(".0")
											? item.stepNumber.split(".")[0]
											: item.stepNumber}
									</div>
								</Tooltip>
								<div className="step-content">
									<Typography variant="body2">{item.description}</Typography>
									{!item.budgetChangeOrderNumber && (
										<>
											<Typography variant="caption">
												{item.assignToUsers.map((user, index) => (
													<span
														key={index}
														className={
															user.isTaskCompleted ? "green-text fw-500" : ""
														}
													>{`${user.fullName}${
														index !== item.assignToUsers.length - 1 ? ", " : ""
													}`}</span>
												))}
											</Typography>
											<div className="step-content-middle">
												<div className="left-part">
													<Typography variant="caption" className="light">
														Duration
													</Typography>
													<Typography variant="caption">
														{item.duration || "-"}
													</Typography>
												</div>
												<div className="right-part">
													<Typography variant="caption" className="light">
														Avg. Duration
													</Typography>
													<Typography variant="caption">
														{item.averageDuration || "-"}
													</Typography>
												</div>
											</div>
										</>
									)}
									{item.budgetChangeOrderNumber && (
										<div className="step-content-middle">
											<span className="budget-co-number">
												{item.budgetChangeOrderNumber}
											</span>
										</div>
									)}
									<div className="step-content-bottom">
										<Tooltip
											title={
												item.completedDate
													? `Completed by ${item.completedBy}`
													: ""
											}
											arrow
											placement="top"
										>
											<div className="left-part">
												<Typography variant="caption" className="light">
													Date Completed
												</Typography>
												<Typography variant="caption">
													{item.completedDate
														? `${Shared.getFormattedDate(
																new Date(item.completedDate),
																"dd/MM/yyyy"
														  )} - ${Shared.getFormattedDate(
																new Date(item.completedDate),
																"hh:mmaaa"
														  )}`
														: "-"}
												</Typography>
											</div>
										</Tooltip>
										{item.comments.length > 0 && (
											// && !item.budgetChangeOrderNumber
											<CustomTooltip
												tooltipStyle={{
													border: "1px solid #034f8b",
												}}
												tooltipProps={{
													title: item.comments.map(
														(cmt: IStepHistoryComment, index: number) => (
															<Grid
																container
																key={index}
																className={index !== 0 ? "history-comment" : ""}
															>
																<Grid item xs={12} className="fw-500">
																	{cmt.fullName}
																</Grid>
																<Grid item xs={12} mt={1}>
																	{cmt.comment}
																</Grid>
															</Grid>
														)
													),
													className: "history-tooltip",
													placement: "right",
													children: (
														<IconButton>
															<CommentIcon />
														</IconButton>
													),
												}}
											/>
										)}
									</div>
								</div>
							</Link>
						)
					)}
			</div>
			{openInfoDialog.open && (
				<ResponsiveDialog
					open={openInfoDialog.open}
					title="Step History Info"
					showActionButtons={false}
					titleCloseIcon={false}
					maxWidth="xs"
					onCancel={() => {}}
				>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography variant="h4" color="black">
								{openInfoDialog.message}
							</Typography>
						</Grid>
						<Grid
							item
							xs={12}
							textAlign="center"
							sx={{ paddingTop: "24px !important" }}
						>
							<Button
								variant="contained"
								onClick={() => {
									setOpenInfoDialog({ open: false, message: "" });
								}}
							>
								Close
							</Button>
						</Grid>
					</Grid>
				</ResponsiveDialog>
			)}
		</div>
	);
};

export default WorkFlowApprovalStepsHistory;
